import { useState } from "react";
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Avatar,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import VerifiedRounded from "@mui/icons-material/VerifiedRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";

const tokens = {
  ink: "#12242E",
  teal: "#0F8B8D",
  tealDark: "#0A5F60",
  line: "rgba(18,36,46,0.10)",
  headingFont: "'Sora', sans-serif",
};

const ClientItem = ({ clientData }) => {
  const [imgError, setImgError] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    banner_pic,
    info,
    title,
    background_c = "#ffffff",
    info_c,
    title_c,
    verified, // only shows the badge if your data actually says so
  } = clientData || {};

  const initials = title?.trim() ? title.trim().charAt(0).toUpperCase() : "?";
  const showImage = banner_pic && !imgError;

  const handleOpen = () => setOpen(true);
  const handleClose = (e) => {
    e?.stopPropagation();
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOpen();
    }
  };

  return (
    <>
      <Card
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        elevation={0}
        sx={{
          width: "98%",
          maxWidth: 1000,
          mx: "auto",
          borderRadius: 3,
          bgcolor: background_c,
          border: "1px solid",
          borderColor: tokens.line,
          boxShadow: "0 2px 10px rgba(18,36,46,0.05)",
          cursor: "pointer",
          transition: "all 0.25s ease",
          "&:hover": {
            boxShadow: "0 10px 24px rgba(18,36,46,0.10)",
            borderColor: tokens.teal,
            transform: "translateY(-2px)",
          },
          "&:focus-visible": {
            outline: `2px solid ${tokens.teal}`,
            outlineOffset: 2,
          },
        }}
      >
        <CardContent sx={{ px: 3, py: 2.5, "&:last-child": { pb: 2.5 } }}>
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Logo, with an initials fallback if the image is missing or fails to load */}
            {showImage ? (
              <Box
                component="img"
                src={banner_pic}
                alt={title ? `${title} logo` : "Client logo"}
                onError={() => setImgError(true)}
                sx={{
                  width: 64,
                  height: 64,
                  objectFit: "contain",
                  borderRadius: 2,
                  bgcolor: "#F5F7F8",
                  p: 1,
                  border: "1px solid",
                  borderColor: tokens.line,
                  flexShrink: 0,
                }}
              />
            ) : (
              <Avatar
                variant="rounded"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  flexShrink: 0,
                  fontFamily: tokens.headingFont,
                  fontWeight: 700,
                  bgcolor: "rgba(15,138,141,0.12)",
                  color: tokens.teal,
                }}
              >
                {initials}
              </Avatar>
            )}

            {/* Text */}
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  noWrap
                  sx={{
                    fontFamily: tokens.headingFont,
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: title_c || tokens.ink,
                  }}
                >
                  {title}
                </Typography>
                {verified && (
                  <VerifiedRounded sx={{ fontSize: 16, color: tokens.teal, flexShrink: 0 }} />
                )}
              </Stack>

              <Typography
                sx={{
                  fontSize: "0.875rem",
                  mt: 0.5,
                  color: info_c || "rgba(18,36,46,0.6)",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {info}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: tokens.teal,
                  mt: 0.75,
                }}
              >
                View details →
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Details modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box sx={{ bgcolor: background_c, px: 3, pt: 4, pb: 2.5, position: "relative" }}>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(18,36,46,0.06)",
              "&:hover": { bgcolor: "rgba(18,36,46,0.12)" },
            }}
          >
            <CloseRounded fontSize="small" />
          </IconButton>

          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center" }}>
            {showImage ? (
              <Box
                component="img"
                src={banner_pic}
                alt={title ? `${title} logo` : "Client logo"}
                sx={{
                  width: 88,
                  height: 88,
                  objectFit: "contain",
                  borderRadius: 2,
                  bgcolor: "#F5F7F8",
                  p: 1.5,
                  border: "1px solid",
                  borderColor: tokens.line,
                }}
              />
            ) : (
              <Avatar
                variant="rounded"
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: 2,
                  fontFamily: tokens.headingFont,
                  fontWeight: 700,
                  fontSize: "1.75rem",
                  bgcolor: "rgba(15,138,141,0.12)",
                  color: tokens.teal,
                }}
              >
                {initials}
              </Avatar>
            )}

            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
              <Typography
                sx={{
                  fontFamily: tokens.headingFont,
                  fontWeight: 700,
                  fontSize: "1.15rem",
                  color: title_c || tokens.ink,
                }}
              >
                {title}
              </Typography>
              {verified && <VerifiedRounded sx={{ fontSize: 18, color: tokens.teal }} />}
            </Stack>
          </Stack>
        </Box>

        <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
          <Typography
            sx={{
              fontSize: "0.95rem",
              lineHeight: 1.7,
              color: info_c || "rgba(18,36,46,0.7)",
              whiteSpace: "pre-line",
            }}
          >
            {info || "No additional details available for this client yet."}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientItem;